import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { LangSwitch, LogoHeader, PROV, PROV_LOGO, ProvBgVideo, provAuth, provClearToken, provToken, useProvLang } from "./provShared";

// /provocative — cinematroische intro voor uitgelogde bezoekers; ingelogde members
// gaan direct naar hun dashboard. De auth-check gebeurt vóór de render: de video
// flitst nooit voor ingelogde gebruikers. Geen "intro al gezien"-conditie.
export default function ProvHome() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { lang, setLang, t } = useProvLang();
  const [gate, setGate] = useState(() => (provToken() ? "checking" : "intro"));

  // canonieke lowercase route: elke case-variant (bv. /Provocative) wordt herschreven
  useEffect(() => {
    if (pathname !== "/provocative") nav("/provocative", { replace: true });
  }, [pathname, nav]);

  useEffect(() => {
    if (!provToken()) return;
    api.get("/provocative/me/dashboard", provAuth())
      .then(({ data }) => {
        if (data.status === "approved") nav("/provocative/dashboard", { replace: true });
        else if (data.status === "rejected") setGate("rejected");
        else setGate("pending");
      })
      .catch((err) => {
        if (err?.response?.status === 401) provClearToken();
        setGate("intro"); // geen geldige sessie → gewoon de intro
      });
  }, [nav]);

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: PROV.bg }} data-testid="prov-home">
      {gate === "intro" && <ProvBgVideo />}
      <div className="absolute top-5 right-6 z-20"><LangSwitch lang={lang} setLang={setLang} /></div>

      {gate === "checking" && (
        <div className="relative z-10 min-h-screen flex items-center justify-center" data-testid="prov-gate-checking">
          <span className="font-mono text-[10px] tracking-[0.3em] text-[#666666]">PROVOCATIVE. …</span>
        </div>
      )}

      {gate === "intro" && (
        <div className="relative z-10 min-h-screen flex flex-col" data-testid="prov-intro">
          <header className="flex justify-center pt-10 md:pt-14 px-6">
            <LogoHeader />
          </header>
          <main className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            <img src={PROV_LOGO} alt="PROVOCATIVE." className="w-[min(88vw,640px)] object-contain" data-testid="prov-title" />
            <div className="font-mono text-xs md:text-sm mt-5" style={{ color: PROV.secondary }} data-testid="prov-phonetic">{t("phonetic")}</div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase mt-2" style={{ color: PROV.muted }} data-testid="prov-pos">{t("pos")}</div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase mt-10" style={{ color: PROV.muted }} data-testid="prov-initiative">{t("initiative")}</div>
            <button onClick={() => nav("/provocative/start")}
              className="mt-10 px-10 py-4 border font-mono text-xs tracking-[0.3em] uppercase transition-colors hover:bg-[#C1C1C1] hover:!text-[#0A0A0A]"
              style={{ borderColor: PROV.border, color: PROV.primary }} data-testid="prov-enter">
              {t("enter")}
            </button>
          </main>
          <footer className="pb-8 text-center font-mono text-[10px] tracking-[0.3em]" style={{ color: PROV.muted }}>
            <a href="/" className="transition-colors hover:text-[#C1C1C1]" data-testid="prov-footer-johlink">JOHBOXING.COM</a>
          </footer>
        </div>
      )}

      {(gate === "pending" || gate === "rejected") && (
        <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid={`prov-${gate}`}>
          <LogoHeader compact />
          <div className="mt-12 border px-6 py-4 font-mono text-[11px] tracking-[0.25em] uppercase"
            style={{ borderColor: gate === "pending" ? PROV.highlight : PROV.muted, color: gate === "pending" ? PROV.primary : PROV.secondary }}
            data-testid="prov-status-box">
            {t(gate === "pending" ? "pending_title" : "rejected_title")}
          </div>
          <p className="mt-6 max-w-md text-sm leading-relaxed" style={{ color: PROV.secondary }} data-testid="prov-status-body">
            {t(gate === "pending" ? "pending_body" : "rejected_body")}
          </p>
          <button onClick={async () => { const tok = provToken(); try { await api.post("/provocative/auth/logout", { token: tok }); } catch { /* ignore */ } provClearToken(); setGate("intro"); }}
            className="mt-10 font-mono text-[10px] tracking-[0.25em] uppercase transition-colors hover:text-[#C1C1C1]"
            style={{ color: PROV.muted }} data-testid="prov-logout">
            {t("logout")}
          </button>
        </div>
      )}
    </div>
  );
}
